// Service Steady PWA — src/hooks/useWeather.ts — v3.0 — built 2026-06-04
//
// Two React Query hooks, both keyed by lat/lon:
//   useWeather()              — compact card (always-on)
//   useWeatherDetail(enabled) — full detail (modal-gated)

import { useQuery } from '@tanstack/react-query';
import {
  fetchWeather,
  fetchWeatherDetail,
  type WeatherCardPayload,
  type WeatherDetailPayload,
} from '@/lib/api/weather';
import { useLocation } from './useLocation';

const TEN_MIN_MS = 10 * 60 * 1000;

export function weatherCardQueryKey(lat: number, lon: number): readonly [string, string] {
  return ['weather:card', `${lat.toFixed(5)},${lon.toFixed(5)}`] as const;
}

export function weatherDetailQueryKey(lat: number, lon: number): readonly [string, string] {
  return ['weather:detail', `${lat.toFixed(5)},${lon.toFixed(5)}`] as const;
}

export function useWeather() {
  const { location } = useLocation();
  return useQuery<WeatherCardPayload>({
    queryKey: weatherCardQueryKey(location.lat, location.lon),
    queryFn: () =>
      fetchWeather({ lat: location.lat, lon: location.lon, name: location.name }),
    staleTime: TEN_MIN_MS,
    refetchOnWindowFocus: false,
    retry: 1,
  });
}

/** Detail hook — fires only when `enabled` is true. The card opens the
 *  modal which flips enabled → true; closing the modal leaves the cached
 *  detail data in place so a second open is instant. */
export function useWeatherDetail(enabled: boolean) {
  const { location } = useLocation();
  return useQuery<WeatherDetailPayload>({
    queryKey: weatherDetailQueryKey(location.lat, location.lon),
    queryFn: () =>
      fetchWeatherDetail({
        lat: location.lat,
        lon: location.lon,
        name: location.name,
      }),
    enabled,
    staleTime: TEN_MIN_MS,
    refetchOnWindowFocus: false,
    retry: 1,
  });
}
